import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import {
  AssignmentLocation,
  assignmentLocationsQuery,
} from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderAssignmentLocationsQuery";
import { useWorkOrderAssignment } from "@/app/ProDrivers/WorkOrders/Hooks/useWorkOrderAssignment";
import { Map } from "@/components/ui/map";
import { Marker, Polyline, Popup } from "react-leaflet";
import L from "leaflet";
import { Badge } from "@/components/catalyst/badge";
import dayjs from "dayjs";
import { useMemo } from "react";
import { HourlyDistanceTable } from "@/components/ui/hourly-distance-table";
import { assignmentStatusColorMap } from "@/app/ProDrivers/WorkOrders/utils/assignmentStatusColorMap";
import { getInitials } from "@/lib/Utils/getInitials";
import colors from "tailwindcss/colors";
import { BasemapLayer } from "@/components/map/BasemapLayer";

const DRIVER_COLOR = colors.red[500];

function createDriverIcon(label: string, color: string) {
  return L.divIcon({
    html: `<div style="background-color: ${color}; width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 12px; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);">${label}</div>`,
    className: "",
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
}

function createLocationIcon(label: string, color: string) {
  return L.divIcon({
    html: `<div style="background-color: ${color}; width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 12px; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);">${label}</div>`,
    className: "",
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

export function AssignmentLocationTab({
  assignmentId,
}: {
  assignmentId: string;
}) {
  const assignment = useWorkOrderAssignment(assignmentId);

  const { data: locationsData, isLoading } = useGqlQuery(
    assignmentLocationsQuery,
    { assignmentIds: [assignmentId] },
    { refetchInterval: 30000 },
  );

  const locationData = useMemo(
    () => locationsData?.assignmentLocations?.[0] ?? null,
    [locationsData],
  );

  const locations = locationData?.locations ?? [];
  const latestLocation = locationData?.latestLocation ?? null;

  const departureCoords =
    assignment.workOrder.departureAddress?.lat &&
    assignment.workOrder.departureAddress?.lng
      ? ([
          assignment.workOrder.departureAddress.lat,
          assignment.workOrder.departureAddress.lng,
        ] as [number, number])
      : null;

  const deliveryCoords =
    assignment.workOrder.deliveryAddress?.lat &&
    assignment.workOrder.deliveryAddress?.lng
      ? ([
          assignment.workOrder.deliveryAddress.lat,
          assignment.workOrder.deliveryAddress.lng,
        ] as [number, number])
      : null;

  const allPoints: [number, number][] = [];
  if (departureCoords) allPoints.push(departureCoords);
  if (deliveryCoords) allPoints.push(deliveryCoords);
  if (latestLocation) {
    allPoints.push([latestLocation.latitude, latestLocation.longitude]);
  }
  locations.forEach((loc) => {
    allPoints.push([loc.latitude, loc.longitude]);
  });

  const center: [number, number] =
    allPoints.length > 0
      ? [
          allPoints.reduce((sum, p) => sum + p[0], 0) / allPoints.length,
          allPoints.reduce((sum, p) => sum + p[1], 0) / allPoints.length,
        ]
      : [-25.2744, 133.7751];

  const calculateZoom = () => {
    if (allPoints.length < 2) return 10;
    const lats = allPoints.map((p) => p[0]);
    const lngs = allPoints.map((p) => p[1]);
    const latDiff = Math.max(...lats) - Math.min(...lats);
    const lngDiff = Math.max(...lngs) - Math.min(...lngs);
    const maxDiff = Math.max(latDiff, lngDiff);

    if (maxDiff < 0.01) return 14;
    if (maxDiff < 0.05) return 12;
    if (maxDiff < 0.1) return 10;
    if (maxDiff < 0.5) return 8;
    if (maxDiff < 1.0) return 6;
    if (maxDiff < 5.0) return 5;
    return 4;
  };

  const routePoints: [number, number][] = locations.map((loc) => [
    loc.latitude,
    loc.longitude,
  ]);

  return (
    <div className="space-y-4">
      {isLoading ? (
        <div className="flex h-[300px] items-center justify-center rounded-lg border">
          <span className="text-zinc-500">Loading map...</span>
        </div>
      ) : (
        <Map
          center={center}
          zoom={calculateZoom()}
          scrollWheelZoom={true}
          height="300px"
          className="overflow-hidden rounded-lg"
        >
          <BasemapLayer />

          {departureCoords ? (
            <Marker
              position={departureCoords}
              icon={createLocationIcon("A", "#3b82f6")}
            >
              <Popup>
                <strong>Pick Up</strong>
                <br />
                {assignment.workOrder.departureAddress?.city}
              </Popup>
            </Marker>
          ) : null}

          {deliveryCoords ? (
            <Marker
              position={deliveryCoords}
              icon={createLocationIcon("B", "#10b981")}
            >
              <Popup>
                <strong>Drop Off</strong>
                <br />
                {assignment.workOrder.deliveryAddress?.city}
              </Popup>
            </Marker>
          ) : null}

          {routePoints.length > 1 ? (
            <Polyline
              positions={routePoints}
              color={DRIVER_COLOR}
              weight={3}
              opacity={0.7}
              dashArray="5, 10"
            />
          ) : null}

          {latestLocation ? (
            <Marker
              position={[latestLocation.latitude, latestLocation.longitude]}
              icon={createDriverIcon(
                getInitials(assignment.proDriver.name),
                DRIVER_COLOR,
              )}
            >
              <Popup>
                <strong>{assignment.proDriver.name}</strong>
                <br />
                <span className="text-xs text-zinc-500">
                  {dayjs.utc(latestLocation.recorded_at).fromNow()}
                </span>
              </Popup>
            </Marker>
          ) : null}
        </Map>
      )}

      <DriverStatusSummary
        driverName={assignment.proDriver.name ?? "—"}
        status={assignment.status}
        locationCount={locations.length}
        latestLocation={latestLocation}
      />

      {locations.length > 0 ? (
        <HourlyDistanceTable locations={locations} />
      ) : (
        <div className="rounded-lg border p-6 text-center">
          <p className="text-sm text-zinc-500">
            No location data recorded yet. Location tracking begins when the
            driver marks their assignment as &quot;On Road&quot;.
          </p>
        </div>
      )}
    </div>
  );
}

function DriverStatusSummary({
  driverName,
  status,
  locationCount,
  latestLocation,
}: {
  driverName: string;
  status: string;
  locationCount: number;
  latestLocation: AssignmentLocation | null;
}) {
  return (
    <div className="flex items-center justify-between rounded-lg border p-3">
      <div className="flex items-center gap-3">
        <div
          className="flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold text-white"
          style={{ backgroundColor: DRIVER_COLOR }}
        >
          {getInitials(driverName)}
        </div>
        <div>
          <div className="text-sm font-medium">{driverName}</div>
          <div className="text-xs text-zinc-500">
            {locationCount} location{locationCount !== 1 ? "s" : ""} recorded
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {latestLocation ? (
          <span className="text-xs text-zinc-500">
            Last seen {dayjs.utc(latestLocation.recorded_at).fromNow()}
          </span>
        ) : (
          <span className="text-xs text-zinc-400">No location data</span>
        )}
        <Badge
          color={
            assignmentStatusColorMap[
              status as keyof typeof assignmentStatusColorMap
            ]
          }
        >
          {status}
        </Badge>
      </div>
    </div>
  );
}
